import { moduleState, state } from 'cerebral'
import { DateTime } from 'luxon'
import { isNull, omitBy } from 'ramda-adjunct'
import { find as rFind, propEq, isNil, isEmpty } from 'ramda'
import schemaSettings from '../../../shared/schemas/event-instance-schema'
import eventRsvpSchemaSettings from '../../../shared/schemas/event-rsvp-schema'
const { eventInstanceResponseSchema, eventInstanceSchema } = schemaSettings
const { eventRsvpSchema } = eventRsvpSchemaSettings
import apiClient from '../../lib/util/api-client'
import { crudActions } from '../factories'

const dashboardService = apiClient.service('event-instances/dashboard')
const reportService = apiClient.service('event-instances/planning-report')
const eventRsvpService = apiClient.service('event-rsvps')

const omitNull = omitBy(isNull)

export const { find, setList, clearList, get, save, remove } = crudActions({ name: 'eventInstances', schemas: { find: eventInstanceResponseSchema } })

const getDate = (date) => {
  if (!date) {
    return null
  }
  const dt = DateTime.fromISO(date, { zone: 'utc' }).startOf('day')
  return dt.isValid ? dt : null
}

export const useTimeRange = ({ props, get }) => {
  if (!props?.query?.date) {
    const { query = {} } = props
    const { startDate, endDate } = get(moduleState`dashboard`) || {}
    const start = getDate(startDate)
    const end = getDate(endDate)
    query.date = start || end ? omitNull({ $gte: start, $lte: end }) : undefined
    delete query._id
    return { query }
  }
}

export const dashboardSetTimeRange = ({ props: { selectedFilter, days, offset, profile = null, todayAsDefault = true }, store, get }) => {
  const timezone = get(state`account.currentUser.timezone`)
  store.set(moduleState`dashboard.selectedFilter`, selectedFilter)
  const now = DateTime.local()
    .setZone(timezone)
    .plus({ days: offset || 0 })
    .setZone('utc', { keepLocalTime: true })
    .startOf('day')

  const hasValidDays = !isNil(days)
  const startDate = hasValidDays ? now.minus({ days }) : null
  const endDate = hasValidDays ? now : null

  store.set(moduleState`dashboard.endDate`, endDate && endDate.toISODate())
  store.set(moduleState`dashboard.startDate`, startDate && startDate.toISODate())
  store.set(moduleState`dashboard.profile`, profile)
  store.set(moduleState`dashboard.todayAsDefault`, todayAsDefault)
  // FIXME: This is a messy approach. It stashes a query onto the route to cause the list to redraw.
  // It also effectively overrides/duplicates the useTimeRange logic above.
  store.set(state`route.params.date`, startDate || endDate ? omitNull({ $gte: startDate?.startOf('day'), $lte: endDate?.endOf('day') }) : { $ne: null })
}

export const setAttendance7d = (context) => {
  dashboardSetTimeRange({ ...context, props: { selectedFilter: 'last-7-days', days: 7, offset: 0, todayAsDefault: false } })
}

export const dashboardSetCalendar = ({ props: { calendar }, store }) => {
  store.set(moduleState`dashboard.selectedCalendar`, calendar)
  // FIXME: This is a messy approach. It stashes a query onto the route to cause the list to redraw.
  if (calendar) {
    store.set(state`route.params.calendars`, calendar)
  } else {
    store.unset(state`route.params.calendars`)
  }
}

export const dashboardSetLedBy = ({ props: { ledBy }, store }) => {
  store.set(moduleState`dashboard.selectedLedBy`, ledBy)
  if (ledBy) {
    store.set(state`route.params.ledBy`, ledBy)
  } else {
    store.unset(state`route.params.ledBy`)
  }
}

export const dashboardDataReset = ({ props: { profile = null }, store }) => {
  store.set(moduleState`dashboard.data`, null)
  store.set(moduleState`dashboard.profile`, profile)
}

export const dashboardRefresh = async ({ store, get }) => {
  const { startDate, endDate, profile, selectedCalendar, selectedLedBy: ledBy } = omitNull(get(moduleState`dashboard`) || {})
  const { eventType = 'Event' } = get(state`route.params`) || {}
  const query = { startDate, endDate, profile, calendar: selectedCalendar, ledBy, eventType }
  const data = (await dashboardService.find({ query })) || null
  store.set(moduleState`dashboard.data`, data)
}

export const getReport = async ({ props }) => {
  const { startDate, endDate, eventType, calendars, mealLocation, skipCache } = props.query
  const report = await reportService.find({ query: { startDate, endDate, eventType, calendars, mealLocation, skipCache } })
  return { report }
}

export const mapCalendarFields = ({ props: { entities }, get }) => {
  const timezone = get(state`account.currentUser.timezone`)
  const labels = get(state`account.eventLabels`)
  const eventLocationCode = get(state`account.eventLocationCode`)

  let { eventInstances } = entities
  const typeCache = {}
  const locations = {}

  eventInstances = eventInstances.map((eventInstance) => {
    let { startAt, endAt, eventType, event: { title, location = '', locationShortCode, allDay, featured, eventLabelRef } = {}, cancelled } = eventInstance

    startAt = DateTime.fromJSDate(startAt).setZone(timezone)
    endAt = DateTime.fromJSDate(endAt).setZone(timezone)

    let eventTitle = title

    if (eventLocationCode && locationShortCode) {
      if (location) {
        locations[locationShortCode] = location
      }
      eventTitle = `${title} [${locationShortCode}]`
    }

    const start = allDay ? startAt.toISODate() : startAt.toISO()
    const end = allDay ? endAt.plus({ day: 1 }).toISODate() : endAt.toISO()

    eventInstance = { ...eventInstance, title: eventTitle, startISO: start, start, end, extendedProps: { shortCode: locationShortCode, featured, cancelled } }

    // Set event color based on the resort settings.
    const labelKey = `${eventType}:${eventLabelRef}`
    const typeLabels = labels[eventType]

    let found = typeCache[labelKey]

    if (!found && typeLabels) {
      found = rFind(propEq(eventLabelRef, '_id'), typeLabels)
    }

    if (found) {
      eventInstance.extendedProps = {
        color: found.color,
        labelColor: found.color,
        labelTextColor: found.textColor,
        shortCode: found.shortCode,
        featured,
        cancelled,
      }
    }

    return eventInstance
  })

  return { eventInstances, locations }
}

export const removeFromWaitlist = async ({ props: { id, item }, eventInstancesService }) => {
  let data = {}
  if (!isEmpty(item)) {
    const { waitlist } = eventInstanceSchema.validateSync({ waitlist: [item] })
    data = { $pull: { waitlist: waitlist[0] } }
  }
  const eventInstance = await eventInstancesService.patch(id, data)
  return { entities: { eventInstances: [eventInstance] } }
}

export const moveToRsvp = async ({ props: { id, profile, count }, eventInstancesService }) => {
  if (profile) {
    const eventRsvp = eventRsvpSchema.validateSync({ eventInstance: id, profile, count })
    const result = await eventRsvpService.create(eventRsvp, { query: { removeFromWaitlist: true } })

    const eventInstance = await eventInstancesService.get(result.eventInstance)
    return { entities: { eventInstances: [eventInstance] } }
  }
}
